import { concatMap, map, switchMap, take, tap, withLatestFrom } from 'rxjs/operators'
import { AppState } from 'src/app/core/core.state'

import { Injectable } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'

import { bookingActions } from './booking.actions'
import { bookingSelectors } from './booking.selectors'
import { BookingAllowedLinkedProductService } from './booking-allowed-linked-product.service'
import { BookingSupervisorBookingService } from './booking-supervisor-booking.service'
import { BookingDocumentService } from './booking-document.service'
import { persistentUserRoleActions } from '../role/user-role/persistent-user-role/persistent-user-role.actions'
import { of } from 'rxjs'
import { persistentUserRoleSelectors } from '../role/user-role/persistent-user-role/persistent-user-role.selector'

@Injectable()
export class BookingRelationEffects {
  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private bookingLinkedProductService: BookingAllowedLinkedProductService,
    private bookingSupervisorBookingService: BookingSupervisorBookingService,
    private bookingDocumentService: BookingDocumentService
  ) {}

  createOneAllowedLinkedProductLink = createEffect(() =>
    this.actions$.pipe(
      ofType(bookingActions.createOneAllowedLinkedProductLink),
      concatMap((action) =>
        this.bookingLinkedProductService
          .createOne(action.bookingId, action.productId)
          .pipe(
            withLatestFrom(
              this.store.select(bookingSelectors.selectById, action.bookingId)
            ),
            map(([response, booking]) =>
              bookingActions.upsertOne({
                entity: {
                  ...booking,
                  allowedLinkedProducts: [
                    ...(booking.allowedLinkedProducts ?? []),
                    action.productId
                  ]
                }
              })
            )
          )
      )
    )
  )

  createManyAllowedLinkedProductLinks = createEffect(() =>
    this.actions$.pipe(
      ofType(bookingActions.createManyAllowedLinkedProductLinks),
      concatMap((action) =>
        this.bookingLinkedProductService.createMany(action.entities).pipe(
          withLatestFrom(this.store.select(bookingSelectors.selectEntities)),
          map(([response, bookingEntities]) =>
            bookingActions.upsertMany({
              entities: action.entities.map((allowedProductLinks) => ({
                ...bookingEntities[allowedProductLinks.bookingId],
                allowedLinkedProducts: [
                  ...(bookingEntities[allowedProductLinks.bookingId]
                    .allowedLinkedProducts ?? []),
                  allowedProductLinks.productId
                ]
              }))
            })
          )
        )
      )
    )
  )

  deleteOneAllowedLinkedProductLink = createEffect(() =>
    this.actions$.pipe(
      ofType(bookingActions.deleteOneAllowedLinkedProductLink),
      concatMap((action) =>
        this.bookingLinkedProductService
          .deleteOne(action.bookingId, action.productId)
          .pipe(
            withLatestFrom(
              this.store.select(bookingSelectors.selectById, action.bookingId)
            ),
            map(([response, booking]) =>
              bookingActions.upsertOne({
                entity: {
                  ...booking,
                  allowedLinkedProducts: [
                    ...booking.allowedLinkedProducts.filter(
                      (productId) => productId !== action.productId
                    )
                  ]
                }
              })
            )
          )
      )
    )
  )

  createOneDocumentLink = createEffect(() =>
    this.actions$.pipe(
      ofType(bookingActions.createOneDocumentLink),
      concatMap((action) =>
        this.bookingDocumentService
          .createOne(action.bookingId, action.documentId)
          .pipe(
            withLatestFrom(
              this.store.select(bookingSelectors.selectById, action.bookingId)
            ),
            map(([response, booking]) =>
              bookingActions.upsertOne({
                entity: {
                  ...booking,
                  documents: [...(booking.documents ?? []), action.documentId]
                }
              })
            )
          )
      )
    )
  )

  deleteOneDocumentLink = createEffect(() =>
    this.actions$.pipe(
      ofType(bookingActions.deleteOneDocumentLink),
      concatMap((action) =>
        this.bookingDocumentService
          .deleteOne(action.bookingId, action.documentId)
          .pipe(
            withLatestFrom(
              this.store.select(bookingSelectors.selectById, action.bookingId)
            ),
            map(([response, booking]) =>
              bookingActions.upsertOne({
                entity: {
                  ...booking,
                  documents: booking.documents?.length
                    ? [
                        ...booking.documents.filter(
                          (documentId) => documentId !== action.documentId
                        )
                      ]
                    : []
                }
              })
            )
          )
      )
    )
  )

  onSetSelectedUserRole = createEffect(
    () =>
      this.actions$.pipe(
        ofType(persistentUserRoleActions.setSelectedUserRole),
        tap((action) => {
          let userRole:any
          this.store.select(persistentUserRoleSelectors.selectSelectedUserRole).pipe(
            take(1)
            ).subscribe((user)=>{
              userRole=user
            })
            userRole&&this.store.dispatch(bookingActions.resetState())})
      ),
    { dispatch: false }
  )
}
